import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import '../../../shared/styles/Orchestrator.css';

function ViewportIndicator({
  boardRef,
  viewportStore,
  channelRef,
  containerRef,
  onMove,
  inline = false,
  mode = 'light',
  cinematicModeActive = false,
  onToggleCinematicMode,
}) {
  const [boardRect, setBoardRect] = useState(null);
  const displayViewport = useSyncExternalStore(
    viewportStore.subscribe,
    viewportStore.getSnapshot,
    viewportStore.getSnapshot
  );
  const isDraggingRef = useRef(false);
  const dragListenersRef = useRef(null);

  useEffect(() => {
    if (!boardRef?.current) return;
    const rect = boardRef.current.getBoundingClientRect();
    setBoardRect({ width: rect.width, height: rect.height });
  }, [boardRef]);

  const cleanupDragListeners = () => {
    if (!dragListenersRef.current) return;
    const { onPointerMove, onPointerUp, handleEl } = dragListenersRef.current;
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerUp);
    document.removeEventListener('pointercancel', onPointerUp);
    document.body.classList.remove('scrollbar-dragging');
    dragListenersRef.current = null;
    isDraggingRef.current = false;
  };

  useEffect(() => cleanupDragListeners, []);

  if (!boardRect || !displayViewport) return null;

  const contentHeight = displayViewport.contentHeight || boardRect.height;
  const viewportHeight = displayViewport.height;
  const scrollTop = displayViewport.scrollTop || 0;
  const scrollLeft = displayViewport.scrollLeft || 0;
  const handleHeightPct = Math.max(5, Math.min(100, (viewportHeight / contentHeight) * 100));
  const maxScroll = contentHeight - viewportHeight;
  const handleTopPct = maxScroll > 0 ? (scrollTop / maxScroll) * (100 - handleHeightPct) : 0;
  const channel = channelRef?.current ?? null;

  const applyScroll = (targetScroll) => {
    if (onMove) {
      onMove(scrollLeft, targetScroll);
    } else if (channel) {
      channel.postMessage({ type: 'managerScroll', scrollTop: targetScroll });
    }
    // If a local container is provided (e.g., GM board wrapper in zoom mode), sync its scroll too
    if (containerRef?.current && maxScroll > 0) {
      const wrapper = containerRef.current;
      const localScrollable = wrapper.scrollHeight - wrapper.clientHeight;
      if (localScrollable > 0) {
        wrapper.scrollTop = Math.round((targetScroll / maxScroll) * localScrollable);
      }
    }
  };

  const handleTrackClick = (event) => {
    if (isDraggingRef.current) return;
    if (!onMove && !channel && !containerRef?.current) return;
    const track = event.currentTarget.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (event.clientY - track.top) / track.height));
    const targetScroll = Math.round(pct * (contentHeight - viewportHeight));
    applyScroll(targetScroll);
  };
  const handlePointerDown = (event) => {
    if (!onMove && !channel && !containerRef?.current) return;
    event.stopPropagation();
    event.preventDefault();
    isDraggingRef.current = true;
    document.body.classList.add('scrollbar-dragging');

    const handleEl = event.currentTarget;
    if (handleEl?.setPointerCapture && event.pointerId != null) {
      try {
        handleEl.setPointerCapture(event.pointerId);
      } catch {
        // ignore (can fail if pointer already captured)
      }
    }

    const track = event.currentTarget.parentElement.getBoundingClientRect();
    const startY = event.clientY;
    const trackHeight = track.height;
    const handleHeightPx = (handleHeightPct / 100) * trackHeight;
    const maxTopPx = Math.max(0, trackHeight - handleHeightPx);
    const startTopPx = (handleTopPct / 100) * maxTopPx;

    const onPointerMove = (moveEvent) => {
      moveEvent.preventDefault();
      const dy = moveEvent.clientY - startY;
      const newTopPx = Math.max(0, Math.min(maxTopPx, startTopPx + dy));
      const scrollRatio = maxTopPx === 0 ? 0 : newTopPx / maxTopPx;
      const targetScroll = Math.round(scrollRatio * (contentHeight - viewportHeight));
      applyScroll(targetScroll);
    };

    const onPointerUp = () => {
      cleanupDragListeners();
    };

    document.addEventListener('pointermove', onPointerMove, { passive: false });
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerUp);

    dragListenersRef.current = { onPointerMove, onPointerUp, handleEl };
  };

  const handleDoubleClick = (event) => {
    if (typeof onToggleCinematicMode !== 'function') return;
    event.preventDefault();
    event.stopPropagation();
    onToggleCinematicMode();
  };

  return (
    <div className={`viewport_indicator${inline ? ' viewport_indicator_inline' : ''}`}>
      <div
        className={`scrollbar_container ${mode === 'dark' ? 'scrollbar_container_dark' : ''}${
          cinematicModeActive ? ' scrollbar_container_cinematic' : ''
        }`}
        onClick={handleTrackClick}
        onDoubleClick={handleDoubleClick}
      >
        <div
          className={`scrollbar_handle${cinematicModeActive ? ' scrollbar_handle_cinematic' : ''}`}
          style={{ top: `${handleTopPct}%`, height: `${handleHeightPct}%` }}
          onPointerDown={handlePointerDown}
          onClick={(event) => event.stopPropagation()}
        />
        {cinematicModeActive && <div className='scrollbar_mode_label'>Cinematic Mode</div>}
      </div>
    </div>
  );
}

export default ViewportIndicator;
